import { i18n } from "@tellescope/i18n";
import type { FormField } from "@tellescope/types-models";
import { emailValidator, phoneValidator } from "@tellescope/validation";
import {
  combineZipCode,
  getRequiredAddressKeys,
  isDateWithinOffsetBounds,
  isValidZipCode,
  parseDateStringAnswer,
  UNKNOWN_DRUG_NAME,
} from "../utils";

/**
 * Bound baked into `@tellescope/validation`'s `numberValidator`. Exceeding it server-side throws
 * "Response must be a number" — a range rejection wearing a type-error label — so number inputs
 * constrain typing to this range rather than letting the value reach submission.
 */
export const NUMBER_RESPONSE_LIMIT = 10000000000000;

const isBlank = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value)) return value.every(isBlank);
  if (typeof value === "object") return Object.values(value).every(isBlank);
  return false;
};

export const isEmptyRichTextValue = (value: unknown): boolean =>
  typeof value === "string" && value.trim() === "<p></p>";

const validateRelatedContact = (contact: Record<string, unknown>): string | null => {
  if (Object.values(contact).every(isBlank)) return i18n.t("At least one field is required", { ns: "forms" });

  const { email, phone, dateOfBirth } = contact;

  if (typeof email === "string" && email !== "") {
    try {
      emailValidator.validate()(email);
    } catch {
      return i18n.t("Email is invalid", { ns: "forms" });
    }
  }

  if (typeof phone === "string" && phone !== "") {
    try {
      phoneValidator.validate()(phone);
    } catch {
      return i18n.t("Phone is invalid", { ns: "forms" });
    }
  }

  if (typeof dateOfBirth === "string" && dateOfBirth !== "" && !parseDateStringAnswer(dateOfBirth))
    return i18n.t("Date of birth should be MM-DD-YYYY", { ns: "forms" });

  return null;
};

/**
 * Additional per-type validation run on top of FORM_FIELD_VALIDATORS_BY_TYPE.
 * Returns an error string on failure, or null when the value is valid.
 */
export const validateFieldByType = (field: FormField, value: unknown): string | null => {
  switch (field.type) {
    case "string":
      // Hard cap is inclusive at 5000 (string-and-stringLong.md §Val #4): a value of exactly
      // 5000 chars fails. The shared @tellescope/validation validator only rejects length > 5000,
      // so this pre-check enforces the documented >= boundary.
      if (typeof value === "string" && value.length >= 5000)
        return i18n.t("Response exceeds maximum length", { ns: "forms" });
      // maxLength errors on an over-long value (00-shared-behavior §3.1#5). This runs before the
      // optional short-circuit, so it applies to optional fields with a present value too.
      if (
        field.options?.maxLength !== undefined &&
        field.options.maxLength !== -1 &&
        typeof value === "string" &&
        value.length > field.options.maxLength
      )
        return i18n.t("Input must not be more than {{count}} characters", {
          ns: "forms",
          count: field.options.maxLength,
        });
      // minLength is not enforced on an empty optional field (00-shared-behavior §3.1#5).
      if (
        field.options?.minLength !== undefined &&
        typeof value === "string" &&
        value.length < field.options.minLength &&
        !(field.isOptional && value.length === 0)
      )
        return i18n.t("Input must be at least {{count}} characters", { ns: "forms", count: field.options.minLength });
      return null;
    case "stringLong":
      // Hard cap is inclusive at 20000 (string-and-stringLong.md §Val #4): a value of exactly
      // 20000 chars fails. See the `string` case above.
      if (typeof value === "string" && value.length >= 20000)
        return i18n.t("Response exceeds maximum length", { ns: "forms" });
      if (
        field.options?.maxLength !== undefined &&
        field.options.maxLength !== -1 &&
        typeof value === "string" &&
        value.length > field.options.maxLength
      )
        return i18n.t("Input must not be more than {{count}} characters", {
          ns: "forms",
          count: field.options.maxLength,
        });
      if (
        field.options?.minLength !== undefined &&
        typeof value === "string" &&
        value.length < field.options.minLength &&
        !(field.isOptional && value.length === 0)
      )
        return i18n.t("Input must be at least {{count}} characters", { ns: "forms", count: field.options.minLength });
      return null;
    case "number":
      // Number bounds are STRICT (number.md / 00-shared-behavior §3.1#6): value <= min and
      // value >= max fail, so the valid range is min+1..max-1 and the bounds themselves are
      // rejected. An answer of 0 is exempt from both, matching legacy's truthiness guard
      // (hooks.tsx:1121-1128) so that an authored `min: 0` still accepts 0.
      if (value !== 0) {
        if (field.options?.min !== undefined && typeof value === "number" && value <= field.options?.min)
          return i18n.t("Input must be at least {{number}}", { ns: "forms", number: field.options.min });
        if (field.options?.max !== undefined && typeof value === "number" && value >= field.options?.max)
          return i18n.t("Input must not be more than {{number}}", { ns: "forms", number: field.options.max });
      }
      if (
        field.options?.minLength !== undefined &&
        typeof value === "number" &&
        String(value).length < field.options?.minLength
      )
        return i18n.t("Input must be at least {{count}} characters", { ns: "forms", count: field.options.minLength });
      if (
        field.options?.maxLength !== undefined &&
        typeof value === "number" &&
        String(value).length > field.options?.maxLength
      )
        return i18n.t("Input must not be more than {{count}} characters", {
          ns: "forms",
          count: field.options.maxLength,
        });
      return null;
    case "email":
      return null;
    case "phone":
      return null;
    case "date": {
      if (!(value instanceof Date)) return null;
      const { minDate, maxDate } = isDateWithinOffsetBounds(value, field.options, { maxBoundary: "endOfDay" });
      if (minDate && value < minDate)
        return i18n.t("Date must be on or after {{date}}", { ns: "forms", date: minDate.toLocaleDateString() });
      if (maxDate && value > maxDate)
        return i18n.t("Date must be on or before {{date}}", { ns: "forms", date: maxDate.toLocaleDateString() });
      return null;
    }
    case "dateString": {
      if (typeof value !== "string" || !value) return null;
      const parts = parseDateStringAnswer(value);
      if (!parts) return i18n.t("Enter a date in MM-DD-YYYY format", { ns: "forms" });
      const parsed = new Date(parts.year, parts.month - 1, parts.day);
      parsed.setHours(0, 0, 0, 0);
      const { minDate, maxDate } = isDateWithinOffsetBounds(parsed, field.options, { maxBoundary: "midnight" });
      if (minDate && parsed < minDate)
        return i18n.t("Date must be on or after {{date}}", { ns: "forms", date: minDate.toLocaleDateString() });
      if (maxDate && parsed > maxDate)
        return i18n.t("Date must be on or before {{date}}", { ns: "forms", date: maxDate.toLocaleDateString() });
      return null;
    }
    case "rating":
      // Rating bounds are INCLUSIVE (unlike `number`, whose bounds are strict) — from/to are
      // the selectable endpoints of the slider. The guards are truthiness checks, so a `from`
      // or `to` of 0 is not enforced as a limit; that matches the slider, where a 0 endpoint is
      // reachable and there is nothing beyond it to reject.
      if (
        typeof value === "number" &&
        ((field.options?.from && value < field.options.from) || (field.options?.to && value > field.options.to))
      )
        return i18n.t("Please enter a number between {{from}} and {{to}}", {
          ns: "forms",
          from: field.options?.from ?? 1,
          to: field.options?.to ?? 10,
        });
      return null;
    case "Time":
      return null;
    case "Timezone":
      return null;
    case "Rich Text":
      if (!field.isOptional && isEmptyRichTextValue(value))
        return i18n.t("{{title}} is required", { ns: "forms", title: field.title });
      return null;
    case "description":
      return null;
    case "multiple_choice": {
      if (!Array.isArray(value)) return null;
      const selectedOptions = value as string[];
      if (field.options?.radio && selectedOptions.length > 1)
        return i18n.t("Only 1 choice is allowed", { ns: "forms" });
      return null;
    }
    case "Dropdown":
      return null;
    case "file":
    case "files": {
      const count = Array.isArray(value) ? value.length : isBlank(value) ? 0 : 1;
      if (count === 0) return null;
      const { min, max } = field.options ?? {};
      if (min !== undefined && count < min)
        return i18n.t("At least {{min}} file(s) are required", { ns: "forms", min });
      if (max !== undefined && count > max) return i18n.t("At most {{max}} file(s) are allowed", { ns: "forms", max });
      return null;
    }
    case "signature": {
      if (typeof value !== "object" || value === null) return null;
      const signature: { signed?: unknown; fullName?: unknown } = value;
      const typedName = typeof signature.fullName === "string" ? signature.fullName : "";
      const nameIsMissing = !typedName.trim();

      if (signature.signed && nameIsMissing) return i18n.t("Please enter your full name", { ns: "forms" });
      if (field.isOptional) return null;
      if (nameIsMissing) return i18n.t("Please enter your full name", { ns: "forms" });
      if (!signature.signed) return i18n.t("Please accept the terms of this signature", { ns: "forms" });
      return null;
    }
    case "ranking":
      return null;
    case "Address": {
      if (typeof value !== "object" || value === null) return null;
      if (!getRequiredAddressKeys(field.options).includes("zipCode")) return null;
      const zipCode = combineZipCode(value);
      if (zipCode === undefined || zipCode.trim().length === 0) return null;
      if (isValidZipCode(zipCode, { fullZIP: field.fullZIP })) return null;
      return field.fullZIP
        ? i18n.t("Please enter a valid zip code (e.g. 12345-6789)", { ns: "forms" })
        : i18n.t("Please enter a valid zip code", { ns: "forms" });
    }
    case "Height": {
      if (typeof value !== "object" || value === null) return null;
      const height = value as { feet?: unknown; inches?: unknown };
      if (typeof height.feet !== "number" || isNaN(height.feet))
        return i18n.t("Feet must be provided", { ns: "forms" });
      if (typeof height.inches !== "number" || isNaN(height.inches))
        return i18n.t("Inches must be provided (enter 0 for no inches)", { ns: "forms" });

      const totalInches = height.feet * 12 + height.inches;
      const { min, max } = field.options ?? {};
      if (min !== undefined && min !== -Infinity && totalInches < min)
        return i18n.t("Height must be at least {{feet}}' {{inches}}\"", {
          ns: "forms",
          feet: Math.floor(min / 12),
          inches: min % 12,
        });
      if (max !== undefined && max !== Infinity && totalInches > max)
        return i18n.t("Height must be no more than {{feet}}' {{inches}}\"", {
          ns: "forms",
          feet: Math.floor(max / 12),
          inches: max % 12,
        });
      return null;
    }
    case "Conditions":
      return null;
    case "Allergies":
      return null;
    case "Medications": {
      if (!Array.isArray(value)) return null;
      if (field.isOptional) return null;
      if (value.length === 0) return i18n.t("At least one medication is required", { ns: "forms" });
      return value.reduce<string | null>((error, medication) => {
        if (error) return error;
        if (typeof medication !== "object" || medication === null) return error;
        const { drugName, otherDrug } = medication as { drugName?: unknown; otherDrug?: unknown };
        const hasDrug = typeof drugName === "string" && !!drugName.trim() && drugName !== UNKNOWN_DRUG_NAME;
        const hasOtherDrug = typeof otherDrug === "string" && !!otherDrug.trim();
        if (hasDrug || hasOtherDrug) return null;
        return i18n.t("A drug selection is required for each medication", { ns: "forms" });
      }, null);
    }
    case "Insurance": {
      if (typeof value !== "object" || value === null) return null;
      const insurance: Record<string, unknown> = { ...value };
      const details: Record<string, unknown> =
        typeof insurance["relationshipDetails"] === "object" && insurance["relationshipDetails"] !== null
          ? { ...insurance["relationshipDetails"] }
          : {};
      const dateOfBirth = details["dateOfBirth"];

      if (typeof dateOfBirth === "string" && dateOfBirth !== "" && !parseDateStringAnswer(dateOfBirth))
        return i18n.t("Enter date of birth in MM-DD-YYYY format", { ns: "forms" });

      if (field.isOptional) return null;

      if (isBlank(insurance["payerName"])) return i18n.t("Insurer is required", { ns: "forms" });
      if (isBlank(insurance["memberId"])) return i18n.t("Member ID is required", { ns: "forms" });

      const relationship = insurance["relationship"];
      if (typeof relationship === "string" && relationship !== "" && relationship !== "Self") {
        if (isBlank(details["fname"])) return i18n.t("First name is required", { ns: "forms" });
        if (isBlank(details["lname"])) return i18n.t("Last name is required", { ns: "forms" });
        if (isBlank(details["gender"])) return i18n.t("Gender is required", { ns: "forms" });
        if (isBlank(dateOfBirth)) return i18n.t("Date of birth is required", { ns: "forms" });
      }

      return null;
    }
    case "Related Contacts": {
      if (!Array.isArray(value)) return null;
      return value.reduce<string | null>((error, contact, index) => {
        if (error) return error;
        if (typeof contact !== "object" || contact === null) return error;

        const contactError = validateRelatedContact(contact as Record<string, unknown>);
        if (!contactError) return null;

        return i18n.t("Contact {{number}}: {{message}}", {
          ns: "forms",
          number: index + 1,
          message: contactError,
        });
      }, null);
    }
    case "Database Select":
      return null;
    case "Appointment Booking":
      return null;
    case "Table Input": {
      if (!Array.isArray(value)) return null;
      const columns = field.options?.tableChoices ?? [];
      return value.reduce<string | null>((error, row, rowIndex) => {
        if (error || !Array.isArray(row)) return error;
        return row.reduce<string | null>((rowError, cell, cellIndex) => {
          if (rowError) return rowError;
          const label = typeof cell?.label === "string" ? cell.label : columns[cellIndex]?.label ?? "";
          const entry = typeof cell?.entry === "string" ? cell.entry : "";
          if (!entry.trim()) {
            return field.isOptional
              ? null
              : i18n.t("Enter a value for {{label}} in row {{row}}", {
                  ns: "forms",
                  label,
                  row: rowIndex + 1,
                });
          }
          // The Date-format check applies to any entered date, so it runs for optional tables too.
          if (columns[cellIndex]?.type === "Date" && !parseDateStringAnswer(entry)) {
            return i18n.t("Enter a date in MM-DD-YYYY format for {{label}} in row {{row}}", {
              ns: "forms",
              label,
              row: rowIndex + 1,
            });
          }
          return null;
        }, null);
      }, null);
    }
    case "Question Group":
      return null;
    case "Hidden Value":
      return null;
    case "Redirect":
      return null;
    case "Stripe":
      return null;
    case "Chargebee":
      return null;
    case "Emotii":
      return null;
    case "Bridge Eligibility":
      return null;
    case "Candid Eligibility":
      return null;
    case "Beluga Patient Preference":
      return null;
    case "Pharmacy Search":
      return null;
  }
};
